import {
  signInWithPopup,
  signInWithCustomToken,
  User,
  type MultiFactorError,
} from 'firebase/auth'
import { isSecondStepRequired } from '../twoFactor'
import {
  InviteOnlyMessages,
  SignInMethodMessages,
} from '../../constants/messages'
import { noInvitationEmail } from './inviteOnly'
import { functionsBaseUrl } from '@/services/functionsUrl'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db, googleProvider, appleProvider } from '../../firebase'
import { UserRoles } from '../../constants/userRoles'
import { extractNameFromDisplayNameOrEmail } from '../userDataValidation'
import { createEnhancedUserDocument } from './user.service'
import { updateLastLogin } from './auth.core'
import { logger } from '../logger'
import { securityService } from '../security'

interface AuthResult {
  success: boolean
  user?: User
  error?: string
  /** Set when Firebase asks for the second sign-in step (PB-62). */
  secondStep?: MultiFactorError
  /** Set when the address has no invitation (invite-only, PB-64). */
  noInvitation?: { email: string }
  /** The person closed the Google / Apple window, or opened a new one
   *  (Firebase cancels the earlier one): not an error to show. */
  cancelled?: boolean
  /** Firebase's error code, when `error` is our own wording (PB-90). */
  code?: string
}

interface OAuthSignInOptions {
  // Skip user doc creation — used by invitation acceptance flow which
  // creates the user doc itself with correct invitation data (corporateAccountId,
  // parentUserId, etc.) rather than a bare doc that needs updating.
  skipUserCreation?: boolean
  // Custom return path for redirect-based OAuth flows (Amazon).
  // Defaults to '/signin'. For invitation acceptance, pass the full
  // accept-invitation path so context is preserved after redirect.
  returnPath?: string
}

/** Parse the Amazon OAuth state parameter (supports both legacy string and encoded JSON) */
function parseAmazonState(
  state: string | null
): { provider: string; returnPath: string } | null {
  if (!state) return null
  try {
    return JSON.parse(atob(state))
  } catch {
    return null
  }
}

/** Extract the return path from Amazon OAuth state in the current URL */
export function getAmazonOAuthReturnPath(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  const state = urlParams.get('state')
  return parseAmazonState(state)?.returnPath || null
}

/**
 * After a Google / Apple sign-in: the person must already have an MNTND user
 * record. MNTND is invitation-only (PB-64): the sign-in page no longer creates
 * a tenant record for anyone who signs in. If there's no record:
 * - the account was just created by an invited person who used the sign-in
 *   page instead of the invitation link: Firebase's account-creation check
 *   (`beforeUserCreated`) put the invitation on the sign-in as
 *   `pendingInvitationId`, so accept it here, as the invitation page would;
 * - otherwise sign them out and report `noInvitation`.
 * The invitation page passes `skipUserCreation` and accepts on its own.
 */
async function finishProviderSignIn(
  user: User,
  provider: 'google' | 'apple',
  options?: OAuthSignInOptions
): Promise<AuthResult> {
  if (!options?.skipUserCreation) {
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    // A deleted record counts as no account (PB-74)
    const deleted = userDoc.data()?.accountStatus === 'deleted'
    if (!userDoc.exists() || deleted) {
      const { claims } = await user.getIdTokenResult()
      const invitationId =
        !deleted && typeof claims.pendingInvitationId === 'string'
          ? claims.pendingInvitationId
          : null
      logger.info('[ProviderSignIn] no user record', {
        provider,
        deleted,
        hasInvitation: !!invitationId,
      })
      if (!invitationId) {
        console.info('[AuthRestore] signOut: provider sign-in, no invitation')
        await auth.signOut()
        return {
          success: false,
          error: InviteOnlyMessages.noInvitationTitle(user.email ?? ''),
          noInvitation: { email: user.email ?? '' },
        }
      }
      // Loaded on demand: the invitation service is large and only this path
      // needs it here.
      const { acceptOAuthInvitation } = await import('../enhancedInvitations')
      const accepted = await acceptOAuthInvitation(
        invitationId,
        user.uid,
        user.email ?? undefined
      )
      if (!accepted.success) {
        logger.error('[ProviderSignIn] invitation acceptance failed', {
          provider,
          invitationId,
          error: accepted.error,
        })
        console.info('[AuthRestore] signOut: invitation acceptance failed')
        await auth.signOut()
        return { success: false, error: accepted.error }
      }
    }
  }

  securityService.logAuthSuccess(user, provider)
  updateLastLogin(user.uid)
  return { success: true, user }
}

/** The error answer shared by Google and Apple sign-in. */
function providerSignInError(error: unknown, label: string): AuthResult {
  if (isSecondStepRequired(error)) {
    return { success: false, error: 'Second step required', secondStep: error }
  }
  const code = (error as { code?: string })?.code
  if (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/user-cancelled'
  ) {
    logger.info(`[ProviderSignIn] ${label} window closed or replaced`, { code })
    return { success: false, cancelled: true }
  }
  // Refused by the account-creation check: no invitation (PB-64)
  const refusedEmail = noInvitationEmail(error)
  if (refusedEmail !== null) {
    logger.info('[ProviderSignIn] refused: no invitation')
    return {
      success: false,
      error: InviteOnlyMessages.noInvitationTitle(refusedEmail),
      noInvitation: { email: refusedEmail },
    }
  }
  // The address already has a password account: Firebase won't merge it
  // on its own; the user connects it from Profile → Security (PB-71)
  if (
    (error as { code?: string })?.code ===
    'auth/account-exists-with-different-credential'
  ) {
    return {
      success: false,
      code: 'auth/account-exists-with-different-credential',
      error: SignInMethodMessages.existsWithPassword,
    }
  }
  logger.error(`Error signing in with ${label}`, error)
  securityService.logAuthFailure((error as Error).message)
  return { success: false, error: (error as Error).message }
}

// Sign in with Google
export const signInWithGoogle = async (
  options?: OAuthSignInOptions
): Promise<AuthResult> => {
  try {
    const { user } = await signInWithPopup(auth, googleProvider)
    return await finishProviderSignIn(user, 'google', options)
  } catch (error) {
    return providerSignInError(error, 'Google')
  }
}

// Sign in with Apple
export const signInWithApple = async (
  options?: OAuthSignInOptions
): Promise<AuthResult> => {
  try {
    const { user } = await signInWithPopup(auth, appleProvider)
    return await finishProviderSignIn(user, 'apple', options)
  } catch (error) {
    return providerSignInError(error, 'Apple')
  }
}

// Sign in with Amazon
// SECURITY NOTE: Login with Amazon JavaScript SDK is safe for web/PWA
// but has security concerns for WebView-based mobile app wrappers.
// See /docs/AMAZON_OAUTH_SECURITY.md for mobile distribution guidance.
// Use TWA (Android) or Safari View Controller (iOS) instead of WebView.
export const signInWithAmazon = async (
  options?: OAuthSignInOptions
): Promise<AuthResult> => {
  try {
    // Check if we're handling a redirect callback with authorization code
    const urlParams = new URLSearchParams(window.location.search)
    const authorizationCode = urlParams.get('code')
    const state = urlParams.get('state')
    const error = urlParams.get('error')

    if (error) {
      throw new Error(`Amazon OAuth error: ${error}`)
    }

    // Check for Amazon OAuth callback — support both legacy string state and encoded JSON state
    const isAmazonCallback =
      authorizationCode &&
      (state === 'amazon_oauth' ||
        parseAmazonState(state)?.provider === 'amazon_oauth')

    if (isAmazonCallback) {
      // Handle the redirect callback
      logger.info('Processing Amazon OAuth callback', {}, 'auth.providers')

      // The build's own project only (functionsUrl.ts allowlist)
      const backendUrl = functionsBaseUrl()

      // Call backend to exchange authorization code for Firebase custom token
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 10000) // 10s timeout

      try {
        const backendResponse = await fetch(`${backendUrl}/amazonAuth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            authorizationCode: authorizationCode,
          }),
          signal: controller.signal,
        })

        clearTimeout(timeout)

        if (!backendResponse.ok) {
          const errorData = await backendResponse.json()
          throw new Error(
            `Backend authentication failed: ${errorData.error || 'Unknown error'}`
          )
        }

        const { customToken, user: amazonUser } = await backendResponse.json()

        // Sign in to Firebase with the custom token
        const userCredential = await signInWithCustomToken(auth, customToken)
        const user = userCredential.user

        if (!options?.skipUserCreation) {
          const userDoc = await getDoc(doc(db, 'users', user.uid))
          if (!userDoc.exists()) {
            const { firstName, lastName } = extractNameFromDisplayNameOrEmail(
              amazonUser.name || '',
              amazonUser.email || ''
            )

            await createEnhancedUserDocument(
              user,
              UserRoles.TENANT,
              { firstName, lastName },
              null, // createdBy
              { accountStatus: 'active' } // OAuth sign-in is verified by provider
            )
          }
        }

        // Clean up URL parameters and redirect to dashboard
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        )
        securityService.logAuthSuccess(user, 'amazon')
        updateLastLogin(user.uid)
        return { success: true, user }
      } catch (fetchError) {
        clearTimeout(timeout)
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw new Error('Amazon OAuth backend request timed out')
        }
        throw fetchError
      }
    }

    // Initiate Amazon OAuth flow
    const clientId = import.meta.env.VITE_AMAZON_CLIENT_ID
    if (!clientId) {
      throw new Error(
        'Amazon Client ID not configured. Please set VITE_AMAZON_CLIENT_ID in .env.local'
      )
    }

    const redirectUri =
      import.meta.env.VITE_AMAZON_REDIRECT_URI || window.location.origin

    // Build Amazon OAuth URL
    // The redirect_uri sent to Amazon must always be /signin (registered with Amazon).
    // The returnPath is encoded in state so we can redirect after the callback completes.
    const returnPath = options?.returnPath || '/signin'
    const statePayload = JSON.stringify({
      provider: 'amazon_oauth',
      returnPath,
    })
    const encodedState = btoa(statePayload)

    const amazonAuthUrl = new URL('https://www.amazon.com/ap/oa')
    amazonAuthUrl.searchParams.set('client_id', clientId)
    amazonAuthUrl.searchParams.set('scope', 'profile')
    amazonAuthUrl.searchParams.set('response_type', 'code')
    amazonAuthUrl.searchParams.set('redirect_uri', `${redirectUri}/signin`)
    amazonAuthUrl.searchParams.set('state', encodedState)

    // Redirect to Amazon for authentication
    logger.info('Redirecting to Amazon OAuth', {}, 'auth.providers')
    window.location.href = amazonAuthUrl.toString()

    // Return pending state since we're redirecting
    return { success: false, error: 'Redirecting to Amazon...' }
  } catch (error) {
    logger.error('Error signing in with Amazon', error)
    securityService.logAuthFailure((error as Error).message)
    return { success: false, error: (error as Error).message }
  }
}
